import {controller, target, targets, attr} from '@github/catalyst'
import {debounce} from '@github/mini-throttle/decorators'

@controller
export class NavListGroupElement extends HTMLElement {
  @attr src: string
  @attr itemsPerPage: number
  @attr maxPages: number
  @attr currentPage = 1

  @target showMoreItem: HTMLElement
  @target list: HTMLUListElement
  @targets focusMarkers: HTMLElement[]
  @target filterContainer: HTMLElement
  @target showFilterButton: HTMLElement
  @target filterInput: HTMLInputElement

  connectedCallback(): void {
    this.toggleShowMoreItemVisibility()
  }

  showFilter() {
    if (!this.filterContainer) return

    this.filterContainer.hidden = false
    this.showFilterButton.hidden = true
    this.filterInput.focus()
  }

  hideFilter() {
    if (!this.filterContainer) return

    this.filterInput.value = ''

    this.filterContainer.hidden = true
    this.showFilterButton.hidden = false
  }

  get totalItems(): number {
    const childCount = this.list.children.length

    if (this.showMoreItem) {
      return childCount - 1
    }

    return childCount
  }

  get atPageLimit(): boolean {
    return this.currentPage === this.maxPages
  }

  get hasEnoughItems(): boolean {
    return this.totalItems < this.itemsPerPage * this.currentPage
  }

  get hideShowMoreItem(): boolean {
    return this.hasEnoughItems || this.atPageLimit
  }

  get showMoreDisabled(): boolean {
    return this.showMoreItem.hasAttribute('aria-disabled')
  }

  set showMoreDisabled(value: boolean) {
    if (value) {
      this.showMoreItem.setAttribute('aria-disabled', 'true')
    } else {
      this.showMoreItem.removeAttribute('aria-disabled')
    }
    this.showMoreItem.classList.toggle('disabled', value)
  }

  private toggleShowMoreItemVisibility(): void {
    if (this.showMoreItem) {
      this.showMoreItem.hidden = this.hideShowMoreItem
    }
  }

  private async showMore(e: Event) {
    e.preventDefault()

    if (this.showMoreDisabled) return
    this.showMoreDisabled = true

    let html
    try {
      const paginationURL = new URL(this.src, window.location.origin)
      this.currentPage++
      paginationURL.searchParams.append('page', this.currentPage.toString())
      const response = await fetch(paginationURL)
      if (!response.ok) return
      html = await response.text()
    } catch (err) {
      // Ignore network errors
      this.showMoreDisabled = false
      this.currentPage--
      return
    }

    const fragment = this.#parseHTML(document, html)
    fragment?.querySelector('li > a')?.setAttribute('data-targets', 'nav-list-group.focusMarkers')
    this.list.insertBefore(fragment, this.showMoreItem)
    this.focusMarkers.pop()?.focus()
    this.showMoreDisabled = false
    this.toggleShowMoreItemVisibility()
  }

  @debounce(200)
  private async filter() {
    if (!this.filterInput) return

    let html
    try {
      const value = this.filterInput.value
      const filterURL = new URL(this.src, window.location.origin)
      filterURL.searchParams.append('q', value)

      const response = await fetch(filterURL)
      if (!response.ok) return

      html = await response.text()
    } catch (err) {
      // Ignore network errors for now
      return
    }

    const fragment = this.#parseHTML(document, html)

    fragment.appendChild(this.showMoreItem) // we need to preserve the "show more" item
    fragment?.querySelector('li > a')?.setAttribute('data-targets', 'nav-list-group.focusMarkers')
    this.list.replaceChildren(fragment)

    this.currentPage = 1 // reset current page as any pagination has been replaced by the query
    this.toggleShowMoreItemVisibility()
  }

  #parseHTML(document: Document, html: string): DocumentFragment {
    const template = document.createElement('template')
    template.innerHTML = html
    return document.importNode(template.content, true)
  }
}
